import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CourseSessionItem } from "../../interfaces/sessions";
import {
  SessionBranchItem,
  SessionGroupItem,
} from "../../interfaces/allocation";
import { getToken } from "../../utils/tokenStore";

const initialState = {
  branchLoading: false,
  groupLoading: false,
  groups: [] as SessionGroupItem[],
  branches: [] as SessionBranchItem[],
  selectedSessionData: {} as CourseSessionItem,
  saveLoading: false,
};

export const getCourseSessionBranches = createAsyncThunk(
  "allocation/getCourseSessionBranches",
  async (
    props: {
      session_id: number | string;
      user_id: any;
      role: string;
      course_id: number | string;
    },
    { rejectWithValue }
  ) => {
    try {
      let query = `?session_id=${props.session_id}&user_id=${props.user_id}&role=${props.role}&course_id=${props.course_id}`;

      const url = `${process.env.REACT_APP_BASE_API_URL}/branches${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if the response has a body
      const responseText = await response.text();

      // Replace NaN with null before parsing JSON
      const cleanedResponse = responseText.replace(/:\s*NaN\s*([,}])/g, ': null$1');

      const data = JSON.parse(cleanedResponse);

      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Authorization failed"
      );
    }
  }
);

export const getCourseSessionGroups = createAsyncThunk(
  "allocation/getCourseSessionGroups",
  async (
    props: {
      session_id: number | string;
      user_id: any;
      role: string;
      course_id: number | string;
    },
    { rejectWithValue }
  ) => {
    try {
      let query = `?session_id=${props.session_id}&user_id=${props.user_id}&role=${props.role}&course_id=${props.course_id}`;

      const url = `${process.env.REACT_APP_BASE_API_URL}/groups${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if the response has a body
      const responseText = await response.text();
      
      // Replace NaN with null before parsing JSON
      const cleanedResponse = responseText.replace(/:\s*NaN\s*([,}])/g, ': null$1');
      
      const data = JSON.parse(cleanedResponse);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const updateSessionSeatAllocation = createAsyncThunk(
  "allocation/updateSessionSeatAllocation",
  async (
    props: {
      session_id: number | string;
      payload: any;
      user_id: any;
      role: string;
      course_id: number | string;
    },
    { rejectWithValue }
  ) => {
    try {
      let query = `?session_id=${props.session_id}&user_id=${props.user_id}&role=${props.role}&course_id=${props.course_id}`;

      const url = `${process.env.REACT_APP_BASE_API_URL}/seat-allocation${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "PUT",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(props.payload),
      }).then((res) => res.json());

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

const allocationSlice = createSlice({
  name: "allocation",
  initialState,
  reducers: {
    setSelectedCourseSessionData: (state, action) => {
      state.selectedSessionData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCourseSessionBranches.pending, (state) => {
        state.branchLoading = true;
      })
      .addCase(getCourseSessionBranches.fulfilled, (state, action) => {
        state.branchLoading = false;
        state.branches = action.payload?.data || [];
      })
      .addCase(getCourseSessionBranches.rejected, (state, action) => {
        state.branchLoading = false;
      })
      .addCase(getCourseSessionGroups.pending, (state) => {
        state.groupLoading = true;
      })
      .addCase(getCourseSessionGroups.fulfilled, (state, action) => {
        state.groupLoading = false;
        state.groups = action.payload?.data || [];
      })
      .addCase(getCourseSessionGroups.rejected, (state, action) => {
        state.groupLoading = false;
      })
      .addCase(updateSessionSeatAllocation.pending, (state) => {
        state.saveLoading = true;
      })
      .addCase(updateSessionSeatAllocation.fulfilled, (state, action) => {
        state.selectedSessionData = {
          ...state.selectedSessionData,
          ...action.payload?.data,
        };
        state.saveLoading = false;
      })
      .addCase(updateSessionSeatAllocation.rejected, (state, action) => {
        state.saveLoading = false;
      });
  },
});

export default allocationSlice.reducer;

export const { setSelectedCourseSessionData } = allocationSlice.actions;
